// src/utils/auditWriterClient.js
// Client-side helper to send audit events to the server-side `auditWriter` function.
import { auth, db } from "../firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";

async function fallbackLocalWrite(payload) {
  try {
    const user = auth.currentUser;
    const entry = {
      ...payload,
      // Mark this document as a client-fallback record so it is handled separately
      clientFallback: true,
      userId: user?.uid || null,
      userEmail: user?.email || null,
      userName: (user && (user.displayName || user.email)) || null,
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent,
      ipAddress: null
    };
    // Write to `auditClientFailures` instead of `auditLogs` to avoid client-origin
    // writes into the canonical audit collection. Admins can review these records.
    await addDoc(collection(db, "auditClientFailures"), entry);
    console.log('[auditWriterClient] Fallback: wrote audit to auditClientFailures');
  } catch (err) {
    console.error('[auditWriterClient] Fallback write failed:', err);
  }
}

export async function sendAuditEvent(payload) {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.warn('[auditWriterClient] No authenticated user; skipping audit event');
      return;
    }

    const url = process.env.REACT_APP_AUDIT_WRITER_URL;
    const idToken = await user.getIdToken();

    if (!url) {
      // No server-side audit writer configured; write locally as a fallback.
      await fallbackLocalWrite(payload);
      return;
    }

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(payload)
    });

    if (!res.ok) {
      console.error('[auditWriterClient] auditWriter POST failed', res.status, await res.text());
      // Do not silently drop audits; as a safe fallback write locally.
      await fallbackLocalWrite(payload);
      return;
    }

    console.log('[auditWriterClient] Audit event sent to auditWriter');
  } catch (error) {
    console.error('[auditWriterClient] Error sending audit event:', error);
    // Best-effort fallback
    try { await fallbackLocalWrite(payload); } catch { /* swallow */ }
  }
}
